// ─────────────────────────────────────────────────────────────────────────────
// characterMovement — carga + validação do JSON de animação de caminhada dos
// PERSONAGENS e MONSTROS (combate / hub).
//
// O CombatGrid lê estes valores em vez de constantes fixas, então dá pra
// afinar o "andar" do jogo (ritmo, pulinho, cascata de inimigos) só editando
// `public/data/movement/character_movement.json` — sem rebuild.
// ─────────────────────────────────────────────────────────────────────────────

import { stripComments } from './MovementProfileLoader';
import type { CharacterMovementConfig } from './schema';

export const DEFAULT_CHARACTER_MOVEMENT: CharacterMovementConfig = {
  version: 1,
  stepMs: 170,
  easing: 'smoothstep',
  // 0 = sem oscilação vertical (o personagem ANDA, estilo 16-bit — a passada é
  // vendida pelo ciclo de frames da folha de sprite). Valores > 0 adicionam um
  // gingado CONTÍNUO (não um pulo por casa).
  bobHeightPx: 0,
  walkFrameMs: 140,
  enemyCascade: { afterPlayerMs: 120, betweenEnemiesMs: 120 },
  clientPathfindingFallback: true,
};

function nonNegNum(v: unknown, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : fallback;
}

/** Aplica defaults + sanitiza um objeto cru (já sem comentários). */
export function normalizeCharacterMovement(raw: unknown): CharacterMovementConfig {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const d = DEFAULT_CHARACTER_MOVEMENT;
  const casc =
    o.enemyCascade && typeof o.enemyCascade === 'object'
      ? (o.enemyCascade as Record<string, unknown>)
      : {};

  return {
    version: typeof o.version === 'number' ? o.version : 1,
    stepMs: Math.max(30, nonNegNum(o.stepMs, d.stepMs)),
    easing: o.easing === 'linear' ? 'linear' : 'smoothstep',
    bobHeightPx: nonNegNum(o.bobHeightPx, d.bobHeightPx),
    walkFrameMs: Math.max(30, nonNegNum(o.walkFrameMs, d.walkFrameMs)),
    enemyCascade: {
      afterPlayerMs: nonNegNum(casc.afterPlayerMs, d.enemyCascade.afterPlayerMs),
      betweenEnemiesMs: nonNegNum(casc.betweenEnemiesMs, d.enemyCascade.betweenEnemiesMs),
    },
    clientPathfindingFallback: o.clientPathfindingFallback !== false,
  };
}

/** Busca e normaliza o JSON. Qualquer falha → defaults (nunca quebra o combate). */
export async function loadCharacterMovementConfig(
  url = '/data/movement/character_movement.json',
): Promise<CharacterMovementConfig> {
  try {
    const res = await fetch(url, { cache: 'no-cache' });
    if (!res.ok) return DEFAULT_CHARACTER_MOVEMENT;
    return normalizeCharacterMovement(stripComments(await res.json()));
  } catch {
    return DEFAULT_CHARACTER_MOVEMENT;
  }
}
